import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Download, FileText, Check, Ban, Clock } from "lucide-react";

const C = { ink: "#1A1E24", slate: "#5B6B7E", line: "#E3E0D8", red: "#B01423", green: "#1E9E63", amber: "#B4740A", navy: "#0B1E3B" };
const TABS = [
  { key: "pending", label: "Started (not signed)" },
  { key: "signed", label: "Awaiting activation" },
  { key: "active", label: "Active" },
  { key: "suspended", label: "Suspended" },
  { key: "free", label: "Free users" },
  { key: "authorities", label: "Debit order authorities" },
  { key: "all", label: "All" },
];
const CHIP = { pending: C.amber, signed: C.navy, active: C.green, suspended: C.red };

const money = (v) => `R${v}`;
const day = (v) => (v ? String(v).slice(0, 10) : "—");

export default function SubscriptionsPage() {
  const [tab, setTab] = useState("signed");
  const [data, setData] = useState({ items: [], counts: {} });
  const [free, setFree] = useState([]);
  const [auths, setAuths] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      if (tab === "authorities") {
        const { data: a } = await api.get("/admin/debit-order/completed");
        setAuths(a.items || []);
        setBusy(false);
        return;
      }
      const { data: d } = await api.get(`/subscriptions${tab === "all" || tab === "free" ? "" : `?status=${tab}`}`);
      setData(d);
      if (tab === "free") {
        const { data: f } = await api.get("/subscriptions/free-users");
        setFree(f.items || []);
      }
    } catch (e) {
      toast.error("Couldn't load subscriptions", { description: e.response?.data?.detail || "" });
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (path, sid, body, ok) => {
    try {
      await api.post(`/subscriptions/${sid}/${path}`, body || {});
      toast.success(ok);
      load();
    } catch (e) {
      toast.error("That didn't work", { description: e.response?.data?.detail || "" });
    }
  };

  const download = async (url, filename) => {
    try {
      const { data: blob } = await api.get(url, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error("Download failed"); }
  };

  const c = data.counts || {};
  return (
    <div data-testid="subscriptions-page">
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, marginBottom: 18 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: C.ink, margin: "0 0 4px", letterSpacing: "-0.02em" }}>Security Package subscriptions</h1>
          <p style={{ fontSize: 13.5, color: C.slate, margin: 0 }}>
            App sign-ups on the freemium plans. Cover activates automatically 24 hours after signing — the buttons here are for exceptions.
          </p>
        </div>
        <button onClick={() => download("/subscriptions/export.csv", "resq1-mandates-signed-vs-loaded.csv")}
          data-testid="subs-export" style={{ padding: "10px 14px", background: C.navy, color: "#fff", border: 0, borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 7, whiteSpace: "nowrap" }}>
          <Download size={15} /> Signed vs loaded (Acuideas)
        </button>
      </div>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
        {[["Started", c.pending || 0], ["Awaiting activation", c.signed || 0], ["Active", c.active || 0], ["Suspended", c.suspended || 0], ["Free users", c.free_users || 0]].map(([l, v]) => (
          <div key={l} style={{ background: "#fff", border: `1px solid ${C.line}`, borderRadius: 12, padding: "10px 14px", minWidth: 118 }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: C.ink, letterSpacing: "-0.02em" }}>{v}</div>
            <div style={{ fontSize: 11.5, color: C.slate }}>{l}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`subs-tab-${t.key}`}
            style={{ padding: "7px 13px", borderRadius: 999, fontSize: 12.5, fontWeight: 600, cursor: "pointer",
                     border: `1px solid ${tab === t.key ? C.navy : C.line}`, background: tab === t.key ? C.navy : "#fff", color: tab === t.key ? "#fff" : C.slate }}>
            {t.label}
          </button>
        ))}
      </div>

      {busy && <div style={{ fontSize: 13, color: C.slate, padding: 10 }}>Loading…</div>}

      {tab === "free" ? (
        <Table head={["Member", "Email", "Cell", "Stage"]} rows={free.map((u) => [u.name || "—", u.email || "—", u.cell || "—",
          <Chip key="s" colour={u.stage === "free" ? C.slate : CHIP[u.stage] || C.slate} label={u.stage === "free" ? "Free app user" : u.stage} />])}
          empty="No free users yet — everyone on the app has cover." testid="subs-free-table" />
      ) : tab === "authorities" ? (
        <Table head={["Member", "Cell", "Partner", "Policy", "Signed", "Via", "Bank account", ""]}
          testid="subs-authorities-table" empty="No completed debit order authorities yet. Issue requests from Communications → Debit Order Authority."
          rows={auths.map((a) => [
            <div key="m"><div style={{ fontWeight: 600 }}>{a.full_name || "—"}</div><div style={{ fontSize: 11.5, color: C.slate }}>{a.email || ""}</div></div>,
            a.cell || "—",
            a.partner || "—",
            <span key="p" style={{ fontFamily: "monospace", fontSize: 12 }}>{a.policy || "—"}</span>,
            day(a.signed_at),
            <Chip key="v" colour={a.channel === "app" ? C.navy : C.green} label={a.channel === "app" ? "In-app" : "Email"} />,
            <div key="b"><div>{a.account_holder || "—"}</div><div style={{ fontSize: 11.5, color: C.slate }}>{[a.bank, a.account].filter(Boolean).join(" · ")}</div></div>,
            a.has_pdf
              ? <button key="pdf" onClick={() => download(`/admin/debit-order/${a.id}/mandate.pdf`, `debit-order-${a.id}.pdf`)} data-testid={`auth-pdf-${a.id}`} style={btn(C.line, C.navy)}><FileText size={13} /> mandate</button>
              : <span style={{ fontSize: 11.5, color: C.slate }}>—</span>,
          ])} />
      ) : (
        <Table head={["Reference", "Member", "Plan", "Status", "Signed", "Activates", "First debit", "Mandate", ""]}
          testid="subs-table" empty="Nothing in this list yet."
          rows={(data.items || []).map((s) => [
            <span key="r" style={{ fontFamily: "monospace", fontSize: 12 }}>{s.ref}</span>,
            <div key="m"><div style={{ fontWeight: 600 }}>{s.main_name || "—"}</div><div style={{ fontSize: 11.5, color: C.slate }}>{s.main_cell || s.main_email}</div></div>,
            <div key="p">{s.plan_label}<div style={{ fontSize: 11.5, color: C.slate }}>{money(s.price)}pm · {(s.members || []).length} covered</div></div>,
            <Chip key="s" colour={CHIP[s.status] || C.slate} label={s.status} />,
            day(s.signed_at), s.activates_at ? day(s.activates_at) : "—", s.first_debit_date || "—",
            s.mandate_loaded_at
              ? <Chip key="ml" colour={C.green} label="loaded" />
              : (s.signed_at ? <button key="ml" onClick={() => act("mandate-loaded", s.id, null, "Marked as loaded")} data-testid={`subs-loaded-${s.ref}`}
                  style={btn(C.line, C.slate)}><Check size={13} /> mark loaded</button> : "—"),
            <div key="a" style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
              {s.signed_at && (
                <button onClick={() => download(`/subscriptions/${s.id}/mandate.pdf`, `debit-order-${s.ref}.pdf`)} data-testid={`subs-pdf-${s.ref}`} style={btn(C.line, C.navy)}>
                  <FileText size={13} /> mandate
                </button>
              )}
              {s.status === "signed" && (
                <button onClick={() => act("activate", s.id, null, "Cover activated")} data-testid={`subs-activate-${s.ref}`} style={btn(C.green, C.green)}>
                  <ShieldCheck size={13} /> activate now
                </button>
              )}
              {s.status === "suspended" && (
                <button onClick={() => act("activate", s.id, null, "Cover reinstated")} data-testid={`subs-reinstate-${s.ref}`} style={btn(C.green, C.green)}>
                  <ShieldCheck size={13} /> reinstate
                </button>
              )}
              {s.status === "active" && (
                <button onClick={() => {
                  const reason = window.prompt("Why is this cover being suspended? (recorded on the audit trail)");
                  if (reason && reason.trim()) act("suspend", s.id, { reason: reason.trim() }, "Cover suspended");
                }} data-testid={`subs-suspend-${s.ref}`} style={btn(C.red, C.red)}>
                  <Ban size={13} /> suspend
                </button>
              )}
            </div>,
          ])} />
      )}

      {tab === "signed" && (data.items || []).length > 0 && (
        <p style={{ fontSize: 12, color: C.slate, marginTop: 12, display: "flex", alignItems: "center", gap: 6 }}>
          <Clock size={13} /> These activate on their own once the 24-hour waiting period passes. "Activate now" only overrides the wait.
        </p>
      )}
    </div>
  );
}

const btn = (border, colour) => ({ padding: "6px 10px", background: "#fff", border: `1px solid ${border}`, borderRadius: 8, fontSize: 11.5, fontWeight: 600, color: colour, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5, whiteSpace: "nowrap" });

const Chip = ({ colour, label }) => (
  <span style={{ display: "inline-block", padding: "3px 9px", borderRadius: 999, fontSize: 11, fontWeight: 700, background: `${colour}18`, color: colour, textTransform: "capitalize" }}>{label}</span>
);

const Table = ({ head, rows, empty, testid }) => (
  <div style={{ background: "#fff", border: `1px solid ${C.line}`, borderRadius: 14, overflow: "hidden" }} data-testid={testid}>
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr>{head.map((h) => (
            <th key={h} style={{ textAlign: "left", padding: "11px 12px", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em", color: C.slate, borderBottom: `1px solid ${C.line}`, whiteSpace: "nowrap" }}>{h}</th>
          ))}</tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={head.length} style={{ padding: 22, textAlign: "center", color: C.slate, fontSize: 13 }}>{empty}</td></tr>
          ) : rows.map((r, i) => (
            <tr key={i}>{r.map((cell, j) => (
              <td key={j} style={{ padding: "11px 12px", borderBottom: `1px solid ${C.line}`, color: C.ink, verticalAlign: "middle" }}>{cell}</td>
            ))}</tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);
